import { useEffect, useMemo, useState, useRef, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Modal, ModalBody, ModalHeader, ModalFooter } from "reactstrap";
import { Box, Badge, useColorMode, Button, Flex, Text, Spinner } from 'theme-ui';
import { ColumnDef } from '@tanstack/react-table';
import DataTable from "../Tabels/Tabel";
import { getAllDeliveriesProjectView, getAllDeliveriesStageView } from "../../store/actions/masterData.action";
import { getTaskByProjectIdSuccess } from "../../store/actions/project.action";
import { TaskByProjectIdApi } from "../../store/api";
import { useLocation } from "react-router-dom";
import { FaChevronDown, FaChevronRight } from "react-icons/fa";
import { BsChatLeftText, BsFileEarmarkText } from "react-icons/bs";
import NotesDetailModal from "./NotesDetailModal";
import SheetIndexModal from "./SheetIndexModal";
import LinkButton from "./LinkButton";
import { getTradeCode } from "../../utils/GenerateQueryString";
import {
  DeliveryRow,
  DeliveryHistoryProps,
  DeliveryModalState,
} from "./DeliveryHistory.types";
import {
  resolveId,
  buildTableRows,
} from "./deliveryHistory.utils";
import { RootState } from "../../../@types";

const TITLE_COLOR = '#38bdf8';
const NOOP = () => { };
const getActionBtnSx = (hasData: boolean, color: string, bg: string) => ({
  border: `1px solid ${hasData ? color : '#94a3b840'}`,
  borderRadius: '6px',
  background: hasData ? bg : 'transparent',
  color: hasData ? color : '#94a3b8',
  p: '4px 8px',
  cursor: hasData ? 'pointer' : 'not-allowed',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  transition: 'all 0.15s',
  opacity: hasData ? 1 : 0.5,
  '&:hover': hasData ? { transform: 'scale(1.08)' } : {},
});

// No | Drawing Title | Trades | Delivery Date | Status | View | Delivery Notes | Sheet Index
const COLUMN_WIDTHS = ['6%', '19%', '11%', '12%', '10%', '22%', '10%', '10%'];

const DeliveryHistory = ({ Open, setOpen, selectedHighlight, actionObject, stageId }: DeliveryHistoryProps) => {
  const location = useLocation();
  const dispatch = useDispatch();
  const { deliveryListProject, deliveryListStage, loader } = useSelector((state: RootState) => state.masterData);
  const taskbyproject = useSelector(
    (state: RootState) => (state as unknown as { project?: { taskbyproject?: Record<string, unknown>[] } })?.project?.taskbyproject
  );
  const [colorMode] = useColorMode();
  const isDark = colorMode === 'dark';
  const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>({});
  const [modalState, setModalState] = useState<DeliveryModalState | null>(null);
  const modalBg = isDark ? '#1e293b' : '#ffffff';
  const headerBg = isDark ? '#0f172a' : '#ffffff';
  const borderColor = isDark ? '#334155' : '#e2e8f0';

  const togglePopover = useCallback(() => setOpen(!Open), [setOpen, Open]);
  const sourceData = stageId ? deliveryListStage?.data : deliveryListProject?.data;
  const isOpen = Open && selectedHighlight === 'DeliveryHistory';
  const isDataLoading = Boolean(loader || (isOpen && !sourceData));
  const projectId = actionObject?.projectId;
  const effectiveStageId = stageId || (actionObject?.stageId as string | undefined) || resolveId(sourceData?.[0]?.stageId);
  const effectiveProjectId = (projectId || resolveId(sourceData?.[0]?.projectId)) as string | undefined;
  const lastFetchedDeliveryRef = useRef<string>('');
  const lastFetchedProjectRef = useRef<string>('');

  useEffect(() => {
    if (!Open) {
      lastFetchedDeliveryRef.current = '';
      lastFetchedProjectRef.current = '';
      return;
    }

    const isEditProject = location.pathname.includes('edit-project');
    const isEditStages = location.pathname.includes('edit-stages');
    const fetchKey = isEditProject && projectId ? `project_${projectId}` : isEditStages && effectiveStageId ? `stage_${effectiveStageId}` : '';
    if (fetchKey && lastFetchedDeliveryRef.current !== fetchKey) {
      lastFetchedDeliveryRef.current = fetchKey;
      dispatch(isEditProject ? getAllDeliveriesProjectView(projectId!) : getAllDeliveriesStageView(effectiveStageId!));
    }

    const hasProjectTasks = Array.isArray(taskbyproject) && taskbyproject.length > 0 && taskbyproject.some(t => {
      const pId = resolveId(t?.projectId);
      return !pId || pId === String(effectiveProjectId);
    });

    if (effectiveProjectId && !hasProjectTasks && lastFetchedProjectRef.current !== String(effectiveProjectId)) {
      lastFetchedProjectRef.current = String(effectiveProjectId);
      TaskByProjectIdApi(effectiveProjectId)
        .then((res: { data?: { data?: Record<string, unknown>[] } }) => {
          if (res?.data?.data) {
            dispatch(getTaskByProjectIdSuccess(res.data.data));
          }
        })
        .catch(() => { });
    }
  }, [Open, location.pathname, projectId, effectiveStageId, effectiveProjectId, taskbyproject, dispatch]);

  const rows = useMemo(() => buildTableRows(sourceData, taskbyproject, isDark), [sourceData, taskbyproject, isDark]);

  const toggleRowExpansion = useCallback((rowId: string) => {
    setExpandedRows(prev => ({ ...prev, [rowId]: !(prev[rowId] ?? true) }));
  }, []);

  const flatTableRows = useMemo(() => {
    if (stageId) return [];
    const list: DeliveryRow[] = [];
    rows.forEach((parent) => {
      list.push(parent);
      const isExpanded = expandedRows[String(parent.id)] ?? true;
      if (isExpanded && parent.subRows && parent.subRows.length > 0) {
        list.push(...parent.subRows);
      }
    });
    return list;
  }, [rows, expandedRows, stageId]);

  const columns = useMemo<ColumnDef<DeliveryRow>[]>(() => [
    {
      header: "No.",
      id: "number",
      cell: ({ row }) => {
        const item = row.original;
        if (item.isChildRow) {
          return (
            <Box sx={{ pl: '12px', textAlign: 'left', color: isDark ? '#94a3b8' : '#64748b' }}>
              {item.number}
            </Box>
          );
        }
        const isExpanded = expandedRows[String(item.id)] ?? true;
        const hasChildren = item.subRows && item.subRows.length > 0;
        return (
          <Box
            onClick={() => hasChildren && toggleRowExpansion(String(item.id))}
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '4px',
              cursor: hasChildren ? 'pointer' : 'default',
              fontWeight: 600,
            }}
          >
            <span>{item.number}</span>
            {hasChildren && (
              <span style={{ color: isDark ? '#94a3b8' : '#64748b', fontSize: '10px' }}>
                {isExpanded ? <FaChevronDown size={10} /> : <FaChevronRight size={10} />}
              </span>
            )}
          </Box>
        );
      }
    },
    {
      header: "Drawing Title",
      id: "drawingTitle",
      cell: ({ row }) => {
        const item = row.original;
        if (item.isChildRow) {
          return (
            <Box sx={{ fontWeight: 500, color: isDark ? '#e2e8f0' : '#334155' }}>
              {item.drawingTitle}
            </Box>
          );
        }
        const color = item.stageColor || (isDark ? '#BB86FC' : '#7743DB');
        return (
          <Badge
            style={{ color, borderColor: color }}
            sx={{
              bg: `${color}20`,
              color: `${color} !important`,
              border: `1px solid ${color} !important`,
              borderRadius: '6px',
              fontSize: '11px',
              fontWeight: 600,
              px: '10px',
              py: '4px',
              whiteSpace: 'nowrap',
            }}
          >
            {item.drawingTitle}
          </Badge>
        );
      }
    },
    {
      header: "Trades",
      id: "trades",
      cell: ({ row }) => {
        const item = row.original;
        const tList = item.trades || [];
        if (!tList.length) return null;
        return (
          <Box sx={{ display: 'flex', gap: '4px', justifyContent: 'center' }}>
            {tList.map(tItem => {
              const code = tItem.code || getTradeCode(tItem.name);
              const color =
                tItem.color && tItem.color !== '#000000' && tItem.color !== '#000'
                  ? tItem.color
                  : isDark
                    ? '#BB86FC'
                    : '#7743DB';
              return (
                <Box
                  key={tItem.name || code}
                  title={tItem.name}
                  className="trade-badge"
                  style={{ color, borderColor: color }}
                  sx={{
                    minWidth: '20px',
                    width: 'auto',
                    height: '20px',
                    px: '3px',
                    borderRadius: '4px',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '10px',
                    fontWeight: 700,
                    bg: `${color}20`,
                    border: `1px solid ${color} !important`,
                    '&&&, &&& *': {
                      color: `${color} !important`,
                    },
                  }}
                >
                  {code}
                </Box>
              );
            })}
          </Box>
        );
      }
    },
    {
      header: "Delivery Date",
      id: "deliveryDate",
      cell: ({ row }) => {
        const item = row.original;
        return (
          <Box sx={{ textAlign: 'center', color: isDark ? '#cbd5e1' : '#475569' }}>
            {item.deliveryDate || ''}
          </Box>
        );
      }
    },
    {
      header: "Status",
      id: "status",
      cell: ({ row }) => {
        const item = row.original;
        const statusColor = isDark ? '#4ade80' : '#16a34a';
        return (
          <Badge
            sx={{
              color: statusColor,
              border: `1px solid ${statusColor}`,
              borderRadius: 6,
              backgroundColor: "transparent",
              px: "8px",
              py: "3px",
              fontSize: "12px",
              fontWeight: 500,
            }}
          >
            {item.status}
          </Badge>
        );
      }
    },
    {
      header: "View",
      id: "view",
      cell: ({ row }) => {
        const item = row.original;
        if (!item.isChildRow) return null;

        const gLinks = item.gdLinks || [];
        const fLinks = item.frontAppLinks || [];
        const pLinks = item.pdfLinks || (item.pdfLink ? [item.pdfLink] : []);
        return (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '4px', justifyContent: 'center', alignItems: 'center' }}>
            <LinkButton
              label="GD Link"
              links={gLinks}
              isDark={isDark}
            />
            <LinkButton
              label="FrontApp"
              links={fLinks}
              isDark={isDark}
            />
            <LinkButton
              label="PDF"
              links={pLinks}
              isPdf
              isDark={isDark}
            />
          </Box>
        );
      }
    },
    {
      header: "Delivery Notes",
      id: "deliveryNotes",
      cell: ({ row }) => {
        const item = row.original;
        if (!item.isChildRow) return null;
        const hasNotes = Boolean(item.rawDescriptions?.length);
        return (
          <Box sx={{ textAlign: 'center' }}>
            <Button
              onClick={() => hasNotes && setModalState({ type: 'notes', row: item })}
              sx={getActionBtnSx(hasNotes, isDark ? '#c084fc' : '#7743DB', isDark ? 'rgba(192, 132, 252, 0.1)' : '#f0eafd')}
              title={hasNotes ? "View delivery notes" : "No data available"}
            >
              <BsChatLeftText size={13} />
            </Button>
          </Box>
        );
      }
    },
    {
      header: "Sheet Index",
      id: "sheetIndex",
      cell: ({ row }) => {
        const item = row.original;
        if (!item.isChildRow) return null;
        const hasSheetIndex = Boolean(item.sheetIndex?.length);
        return (
          <Box sx={{ textAlign: 'center' }}>
            <Button
              onClick={() => hasSheetIndex && setModalState({ type: 'sheet', row: item })}
              sx={getActionBtnSx(hasSheetIndex, isDark ? '#38bdf8' : '#0284c7', isDark ? 'rgba(56, 189, 248, 0.1)' : '#e0f2fe')}
              title={hasSheetIndex ? "View sheet index" : "No data available"}
            >
              <BsFileEarmarkText size={13} />
            </Button>
          </Box>
        );
      }
    }
  ], [isDark, expandedRows, stageId]);

  const stageModalData = useMemo(() => {
    if (!stageId || !rows.length) return null;
    const firstRow = rows[0];
    const combinedDescriptions = rows.flatMap(r =>
      r.subRows && r.subRows.length > 0
        ? r.subRows.flatMap(sr => sr.rawDescriptions || [])
        : r.rawDescriptions || []
    );
    const sName =
      firstRow?.subRows?.find(sr => /combined delivery/i.test(sr.drawingTitle))?.drawingTitle ||
      firstRow?.subRows?.[0]?.drawingTitle ||
      firstRow?.drawingTitle ||
      '';
    return {
      stageName: sName,
      stageColor: firstRow?.stageColor,
      descriptions: combinedDescriptions,
      trades: firstRow?.trades,
    };
  }, [stageId, rows]);

  const closeButton = useMemo(
    () => (
      <button
        type="button"
        aria-label="Close"
        onClick={togglePopover}
        style={{
          color: isDark ? '#94a3b8' : '#64748b',
          background: 'transparent',
          border: 'none',
          fontSize: '22px',
          lineHeight: 1,
          cursor: 'pointer',
        }}
      >
        <span aria-hidden="true">&times;</span>
      </button>
    ),
    [isDark, togglePopover]
  );

  if (stageId) {
    return (
      <NotesDetailModal
        isOpen={isOpen}
        onClose={togglePopover}
        stageName={stageModalData?.stageName || ''}
        stageColor={stageModalData?.stageColor}
        descriptions={stageModalData?.descriptions || []}
        trades={stageModalData?.trades}
        isDark={isDark}
        isLoading={isDataLoading}
      />
    );
  }

  return (
    <>
      <Modal
        isOpen={isOpen}
        centered
        size="xl"
        toggle={togglePopover}
        style={{ maxWidth: '93%', width: '93%', height: '80vh' }}
        contentClassName="h-100 d-flex flex-column"
      >
        <ModalHeader
          toggle={togglePopover}
          close={closeButton}
          style={{
            backgroundColor: headerBg,
            borderBottom: `1px solid ${borderColor}`,
            padding: '16px 24px',
            flexShrink: 0,
          }}
        >
          <Flex sx={{ gap: "1rem" }}>
            <Text sx={{ color: TITLE_COLOR, fontWeight: 700, fontSize: '16px', letterSpacing: '0.05em' }}>
              {'DELIVERY HISTORY'}
            </Text>
          </Flex>
        </ModalHeader>
        <ModalBody
          style={{
            backgroundColor: modalBg,
            padding: '16px 20px',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            minHeight: 0,
          }}
        >
          {isDataLoading ? (
            <Flex sx={{ justifyContent: 'center', alignItems: 'center', minHeight: '300px' }}>
              <Spinner size={36} />
            </Flex>
          ) : (
            <Box
              sx={{
                width: '100%',
                flex: 1,
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column',
                '& .table-responsive, & .table-responsive > div': {
                  flex: 1,
                  minHeight: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  paddingLeft: '0 !important',
                  paddingRight: '0 !important',
                },
                '& .tableFixHead': { flex: 1, minHeight: 0, height: '100%', maxHeight: '100% !important', overflowX: 'hidden' },
                // fixed layout so all columns fit the modal width (no horizontal scroll)
                '& table': { tableLayout: 'fixed', width: '100%' },
                ...Object.fromEntries(COLUMN_WIDTHS.map((width, i) => [`& th:nth-of-type(${i + 1})`, { width }])),
              }}
            >
              <DataTable
                data={flatTableRows}
                columns={columns}
                showNavigation={false}
                onPageChange={NOOP}
                onPerPageChange={NOOP}
                perPageCount={flatTableRows.length || 10}
                TotalLength={flatTableRows.length}
                enableColumnFilters={false}
                noDataCentered={true}
                inlineLoader={false}
              />
            </Box>
          )}
        </ModalBody>
        <ModalFooter style={{ backgroundColor: modalBg, borderTop: `1px solid ${borderColor}`, flexShrink: 0 }}>
          <Button variant="buttons.CancelButton" onClick={togglePopover}>
            Close
          </Button>
        </ModalFooter>
      </Modal>

      {/* Separate Delivery Notes / Sheet Index Modal */}
      {modalState?.row && (modalState.type === 'notes' ? (
        <NotesDetailModal
          isOpen
          onClose={() => setModalState(null)}
          stageName={modalState.row.drawingTitle}
          stageColor={modalState.row.stageColor}
          descriptions={modalState.row.rawDescriptions}
          trades={modalState.row.trades}
          isDark={isDark}
          closeButtonText="Back"
        />
      ) : (
        <SheetIndexModal
          isOpen
          onClose={() => setModalState(null)}
          stageName={modalState.row.drawingTitle}
          stageColor={modalState.row.stageColor}
          sheetIndex={modalState.row.sheetIndex || []}
          trades={modalState.row.trades}
          isDark={isDark}
          closeButtonText="Back"
        />
      ))}
    </>
  );
};

export default DeliveryHistory;
